import { createSelector } from '../reselect';
import { totalOrders } from '../generalSelectors';
import {
  emptyArray,
  emptyObject,
  getNewStats,
  isOldStats,
  safeDivide,
  simpleChart,
  simpleChart2,
} from '../generalUtils';

const isOldStatsTotal = (total) => {
  return !(total && total.total);
};

const summarizeSources = (stats, source) => {
  const allTotal = stats.total.map((s) => s.responses).reduce((a, b) => a + b, 0);
  const sourceTotal = stats[source];
  if (!sourceTotal || sourceTotal.length === 0 || !allTotal) {
    return 0;
  }
  const sourceTotalSum = sourceTotal.map((s) => s.responses).reduce((a, b) => a + b, 0);
  const percent = safeDivide(sourceTotalSum, allTotal) * 100;
  return percent;
};

const summarizeSourcesChart = (stats, source) => {
  const allTotalsChart = simpleChart(stats.enquirelabs, 'total');
  const sourceTotalsChart = simpleChart(stats.enquirelabs, source);
  return simpleChart2(sourceTotalsChart, allTotalsChart, { chart1yMultiplier: 100 });
};

const summarizeSourcesOldInfra = (total, source) => {
  if (!total) return 0;
  const allTotal = Object.values(total).flat();
  if (!total[source] || total[source].length === 0 || !allTotal) {
    return 0;
  }
  const percent = safeDivide(total[source].length, allTotal.length) * 100;
  return percent;
};

const getTotalEnqirelabsDay = (stats) => {
  if (isOldStats(stats)) {
    return getTotalEnqirelabsDayOldInfra(stats);
  }

  if (!stats.enquirelabs) {
    return {};
  }
  const dayVal = {
    'facebook-ads': [],
    'google-ads': [],
    'snapchat-ads': [],
    'tiktok-ads': [],
    'pinterest-ads': [],
    'instagram-ads': [],
    other: [],
    total: [],
  };
  stats.enquirelabs.forEach((day_hours) => {
    Object.keys(day_hours.hours).forEach((hour) => {
      const enquirelabs = day_hours.hours[hour];
      Object.keys(enquirelabs || {}).forEach((source) => {
        if (dayVal[source]) {
          dayVal[source].push(enquirelabs[source]);
        }
      });
    });
  });
  return dayVal;
};

const getTotalEnqirelabsDayOldInfra = (stats): Record<string, any[]> => {
  const dayVal = {
    facebook: [],
    google: [],
    snapchat: [],
    tiktok: [],
    pinterest: [],
    instagram: [],
  };
  stats.forEach((day_hours) => {
    Object.keys(day_hours.hours).forEach((hour) => {
      const { enquirelabs } = day_hours.hours[hour];
      Object.keys(enquirelabs || {}).forEach((source) => {
        if (dayVal[source]) {
          dayVal[source].push(...enquirelabs[source]);
        }
      });
    });
  });
  return dayVal;
};

const getEnquirelabsStats = (state) => {
  if (state.enquirelabsApiKey) {
    return state.newStats || emptyObject;
  }
  return state.stats;
};

const totalEnqirelabsDay = createSelector([getEnquirelabsStats], getTotalEnqirelabsDay);

const responsesPrecent = createSelector(
  [totalEnqirelabsDay, totalOrders],
  (enquireLabsList, orders) => {
    let percent;
    if (!enquireLabsList) {
      return 0;
    }
    if (isOldStatsTotal(enquireLabsList)) {
      percent = safeDivide(Object.values(enquireLabsList).flat().length, orders) * 100;
    } else {
      let allTotal = 0;
      if (enquireLabsList.total.length > 0) {
        allTotal = enquireLabsList.total.map((s) => s.responses).reduce((a, b) => a + b, 0);
      }
      percent = safeDivide(allTotal, orders) * 100;
    }
    return percent;
  }
);

const responsesPrecentChart = createSelector([], () => {
  return [];
});

const totalFacebookEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'facebook')
    : summarizeSources(total, 'facebook-ads')
);

const totalFacebookEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'facebook-ads')
);

const totalGoogleEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'google')
    : summarizeSources(total, 'google-ads')
);

const totalGoogleEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'google-ads')
);

const totalSnapchatEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'snapchat')
    : summarizeSources(total, 'snapchat-ads')
);

const totalSnapchatEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'snapchat-ads')
);

const totalTiktokEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'tiktok')
    : summarizeSources(total, 'tiktok-ads')
);

const totalTiktokEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'tiktok-ads')
);

const totalPinterestEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'pinterest')
    : summarizeSources(total, 'pinterest-ads')
);

const totalPinterestEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'pinterest-ads')
);

const totalInstagramEnq = createSelector([totalEnqirelabsDay], (total) =>
  isOldStatsTotal(total)
    ? summarizeSourcesOldInfra(total, 'instagram')
    : summarizeSources(total, 'instagram-ads')
);

const totalInstagramEnqChart = createSelector([getNewStats], (stats) =>
  isOldStats(stats) ? emptyArray : summarizeSourcesChart(stats, 'instagram-ads')
);

const PROPS_TO_SELECTORS = {};

export default {
  PROPS_TO_SELECTORS,
  SELECTORS: {
    totalEnqirelabsDay,
    totalFacebookEnq,
    responsesPrecent,
    totalGoogleEnq,
    totalSnapchatEnq,
    totalTiktokEnq,
    totalPinterestEnq,
    totalInstagramEnq,
    responsesPrecentChart,
    totalFacebookEnqChart,
    totalGoogleEnqChart,
    totalSnapchatEnqChart,
    totalTiktokEnqChart,
    totalPinterestEnqChart,
    totalInstagramEnqChart,
  },
  getTotalEnqirelabsDay,
  summarizeSources,
};
